import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import { useFlexLayout, useTable } from 'react-table';
import classnames from 'classnames';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';

import { useIsShipStationAnalyticsFeatureEnabled } from '../../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import { emptyArray } from 'ui-components/default-props/empty';
import commonMessages from '../common-messages';
import { getTotalCount } from '../helper';

import style from '../common-styles.module.less';
import analyticsStyle from '../../analytics-style.module.less';

const TITLE = 'title';
const NAME = 'name';
const QTY = 'qty';
const SHARE = 'share';

const dataSourcesToAlign = [QTY, SHARE];

const messages = defineMessages({
  [TITLE]: {
    defaultMessage: 'Sales by Category',
    id: 'C+2/Fr',
    description: 'sales by category table header',
  },
  [NAME]: {
    defaultMessage: 'Name',
    id: 'kEQ33Y',
    description: 'sales by category table name header',
  },
  [QTY]: {
    defaultMessage: 'Qty',
    id: 'Yw6+fG',
    description: 'sales by category table quantity (abbreviated) header',
  },
  [SHARE]: {
    defaultMessage: 'Share',
    id: 'aWeRRv',
    description: 'sales by category table share header',
  },
});

type CategoryData = {
  name: string;
  count: number;
  amount?: number;
};

type SalesByCategoryTableProps = {
  data?: CategoryData[];
};

export const SalesByCategoryTable = ({ data = emptyArray as CategoryData[] }: SalesByCategoryTableProps) => {
  const intl = useIntl();
  const isShipStationAnalyticsFeatureEnabled = useIsShipStationAnalyticsFeatureEnabled();

  const emptyRow = !data.length ? <FormattedMessage {...commonMessages.nullStateMessage} /> : null;

  const renderAlignedCell = useCallback(
    (value: React.ReactNode, accessor: string) => {
      if (dataSourcesToAlign.includes(accessor)) {
        return (
          <div
            className={classnames(style.right, {
              [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
            })}
          >
            {value}
          </div>
        );
      }
      return isShipStationAnalyticsFeatureEnabled ? (
        <div className={classnames(analyticsStyle['analytics-table-header'], analyticsStyle.left)}>{value}</div>
      ) : (
        value
      );
    },
    [isShipStationAnalyticsFeatureEnabled],
  );

  const renderAlignedHeader = useCallback(
    (accessor: string, title: React.ReactNode) => {
      if (dataSourcesToAlign.includes(accessor)) {
        return (
          <div
            className={classnames(style.right, {
              [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
            })}
          >
            {title}
          </div>
        );
      }
      return isShipStationAnalyticsFeatureEnabled ? (
        <div className={classnames(analyticsStyle['analytics-table-header'], analyticsStyle.left)}>{title}</div>
      ) : (
        title
      );
    },
    [isShipStationAnalyticsFeatureEnabled],
  );

  const columns = useMemo(
    () => [
      {
        Header: () => renderAlignedHeader(NAME, intl.formatMessage(messages[NAME])),
        accessor: NAME as typeof NAME,
        Cell: ({ value }: { value: string }) => renderAlignedCell(value, NAME),
      },
      {
        Header: () => renderAlignedHeader(QTY, intl.formatMessage(messages[QTY])),
        accessor: QTY as typeof QTY,
        Cell: ({ value }: { value: string }) => renderAlignedCell(value, QTY),
      },
      {
        Header: () => renderAlignedHeader(SHARE, intl.formatMessage(messages[SHARE])),
        accessor: SHARE as typeof SHARE,
        Cell: ({ value }: { value: string }) => renderAlignedCell(value, SHARE),
      },
    ],
    [intl, renderAlignedCell, renderAlignedHeader],
  );

  const tableData = useMemo(() => {
    const totalCount = getTotalCount(data);
    return data.map((category) => ({
      name: category.name,
      qty: intl.formatNumber(category.count),
      share: intl.formatNumber(totalCount !== 0 ? category.count / totalCount : 0, { style: 'percent' }),
    }));
  }, [data, intl]);

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data: tableData,
    },
    useFlexLayout,
  );

  return (
    <>
      <div
        className={classnames({
          [style['table-title']]: !isShipStationAnalyticsFeatureEnabled,
          [analyticsStyle['analytics-table-title']]: isShipStationAnalyticsFeatureEnabled,
        })}
      >
        <Text textType={TextType.H3} value={messages[TITLE]} />
      </div>
      <ReactTableWrapper
        className={classnames({
          [analyticsStyle['analytics-table']]: isShipStationAnalyticsFeatureEnabled,
        })}
        emptyRow={emptyRow}
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        rows={rows}
      />
    </>
  );
};

export default SalesByCategoryTable;
